import type { InstagramMedia, MediaInsights } from '../../../shared/api/reports.api';

export type ImageItem = { id: string; thumbnailUrl: string };

export type ImageResult = {
  type: 'result';
  id: string;
  imageUrl: string;
};

export type ImageUrlsCallbacks = {
  onResult: (result: ImageResult) => void;
  onProgress: (done: number, total: number) => void;
};

// Streams the backend image-hosting endpoint (Fastify, proxied at /api): it
// re-hosts each thumbnail on a stable public URL. Theme OCR happens separately
// via the Supabase edge function. Parses Server-Sent Events line by line.
export async function fetchReportImageUrls(
  items: ImageItem[],
  callbacks: ImageUrlsCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/reports/image-urls', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Ошибка сервера отчёта: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const event = JSON.parse(line.slice(5).trim()) as
        | ImageResult
        | { type: 'progress'; done: number; total: number }
        | { type: 'done' }
        | { type: 'error'; message: string };

      if (event.type === 'result') callbacks.onResult(event);
      else if (event.type === 'progress') callbacks.onProgress(event.done, event.total);
      else if (event.type === 'error') throw new Error(event.message);
    }
  }
}

/** Строка серверного сбора: медиа + метрики в исходном виде Graph API. */
export type CollectedStatsRow = {
  type: 'row';
  index: number;
  media: InstagramMedia;
  insights: Partial<MediaInsights>;
  insightsMissing: boolean;
  failed: boolean;
};

export type CollectStatsCallbacks = {
  onListed: (total: number) => void;
  onRow: (row: CollectedStatsRow) => void;
  onProgress: (done: number, total: number) => void;
};

/**
 * Серверный сбор статистики аккаунта: бэкенд гоняет те же запросы к Graph API,
 * что раньше делал браузер (см. report-collect.service в бэкенде), но с большей
 * параллельностью и без токена на клиенте. Формат SSE тот же, что у
 * image-urls. Ошибка транспорта или сервера бросается — вызывающая сторона
 * откатывается на прежний браузерный сбор.
 */
export async function fetchCollectedStats(
  params: { accountId: string; from: Date; to: Date },
  callbacks: CollectStatsCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/reports/collect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accountId: params.accountId,
      from: params.from.toISOString(),
      to: params.to.toISOString(),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Серверный сбор статистики недоступен: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const event = JSON.parse(line.slice(5).trim()) as
        | CollectedStatsRow
        | { type: 'listed'; total: number }
        | { type: 'progress'; done: number; total: number }
        | { type: 'done' }
        | { type: 'error'; message: string };

      if (event.type === 'row') callbacks.onRow(event);
      else if (event.type === 'listed') callbacks.onListed(event.total);
      else if (event.type === 'progress') callbacks.onProgress(event.done, event.total);
      else if (event.type === 'done') finished = true;
      else if (event.type === 'error') throw new Error(event.message);
    }
  }

  // Оборванный посреди потока ответ — не повод отдать половину статистики.
  if (!finished) throw new Error('Серверный сбор оборвался до завершения');
}

export type EnrichEntry = { imageUrl?: string; theme?: string };

/**
 * Общий кэш отчёта на команду: что уже посчитано по этим mediaId (тема,
 * перезалитое превью). Кэш вспомогательный, поэтому любая ошибка — просто
 * пустой ответ: отчёт соберётся, как собирался без него. Пачки по 800:
 * сервер ограничивает запрос двумя тысячами id, а годовой отчёт крупного
 * аккаунта в лимит не влезает; сбой одной пачки не топит остальные.
 */
export async function fetchEnrichEntries(ids: string[]): Promise<Record<string, EnrichEntry>> {
  const out: Record<string, EnrichEntry> = {};
  for (let i = 0; i < ids.length; i += 800) {
    try {
      const res = await fetch('/api/reports/enrich', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: ids.slice(i, i + 800) }),
      });
      if (!res.ok) continue;
      const data = (await res.json()) as { entries?: Record<string, EnrichEntry> };
      Object.assign(out, data.entries ?? {});
    } catch {
      // пачка не ответила — просто посчитаем её публикации заново
    }
  }
  return out;
}

/**
 * Обратная запись распознанных тем в общий кэш — тихая, отчёт её не ждёт.
 * Пачки по 300 под серверный лимит в пятьсот записей.
 */
export async function saveEnrichThemes(items: { id: string; theme: string }[]): Promise<void> {
  for (let i = 0; i < items.length; i += 300) {
    try {
      await fetch('/api/reports/enrich/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.slice(i, i + 300) }),
      });
    } catch {
      // не мешаем отчёту
    }
  }
}

export type ServerPostViews = {
  id: string;
  permalink: string | null;
  timestamp: string;
  views: number;
  /** Ролик из «Монтажа» направления B2G. */
  b2g: boolean;
};

export type AverageViewsCallbacks = {
  onAccount: (accountId: string, posts: ServerPostViews[]) => void;
  onAccountError: (accountId: string, message: string) => void;
};

/**
 * «Средние просмотры» с сервера (/api/reports/average-views): все аккаунты
 * одним запросом, результат по аккаунту по мере готовности. Ошибка транспорта
 * или старый бэкенд — бросается; вызывающий досчитывает оставшееся из браузера.
 */
export async function fetchAverageViews(
  params: { accountIds: string[]; from: Date; to: Date },
  callbacks: AverageViewsCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/reports/average-views', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accountIds: params.accountIds,
      from: params.from.toISOString(),
      to: params.to.toISOString(),
    }),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Серверный расчёт недоступен: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const event = JSON.parse(line.slice(5).trim()) as
        | { type: 'account'; accountId: string; posts: ServerPostViews[] }
        | { type: 'account-error'; accountId: string; message: string }
        | { type: 'done' }
        | { type: 'error'; message: string };
      if (event.type === 'account') callbacks.onAccount(event.accountId, event.posts);
      else if (event.type === 'account-error') callbacks.onAccountError(event.accountId, event.message);
      else if (event.type === 'done') finished = true;
      else if (event.type === 'error') throw new Error(event.message);
    }
  }
  if (!finished) throw new Error('Серверный расчёт оборвался до завершения');
}
